import { css } from '@emotion/react'
import { useState } from 'react'
import type { OnChangeEventContext } from '../../../shared/input/Dropdown'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../../shared/modal'
import { ModalSize } from '../../../shared/modal/Modal/Modal'
import { BoardGroupFragment } from '../../../../generated/graphql'
import { DropdownMode } from '../../../shared/input/Dropdown'
import { RadioGroup, Select } from '../../../shared/input'
import { PrimaryButton, SecondaryButton } from '../../../shared/buttons'
import { ButtonType } from '../../../shared/buttons/types'
import { tertiaryBodyCss } from '../../../../lib/styles/typography'
import useAppSettings from '../../../../lib/hooks/useAppSettings'

export type MoveGroupState = {
  destinationGroupId?: string
  destinationType: string
  selectedGroupId: string
}

const radioOptions = [
  {
    label: 'Move Before',
    value: 'BEFORE'
  },
  {
    label: 'Move After',
    value: 'AFTER'
  }
]

const upperFormCss = css`
  padding-top: 8px;
`
const lowerFormCss = css`
  padding-top: 24px;
  padding-bottom: 24px;
`
const dropdownCss = css`
  width: 280px;
`

export type MoveGroupModalProps = {
  /** The initial group ID  */
  groupId: string
  /** List of all plan groups */
  groups: Array<BoardGroupFragment>
  /** Indicates if the modal is open */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel: () => void
  /** Event callback when the form is submitted */
  onSave: (saveState: MoveGroupState) => void
}

const MoveGroupModal = ({ groupId, groups, isOpen, onCancel, onSave }: MoveGroupModalProps) => {
  const { text } = useAppSettings()
  const termLabel = text.term

  const [moveGroupState, setMoveGroupState] = useState<MoveGroupState>({
    selectedGroupId: groupId,
    destinationType: 'BEFORE'
  })

  const handleChangeSelectedGroup = (e: OnChangeEventContext) => {
    const group = e.value as BoardGroupFragment
    setMoveGroupState({ ...moveGroupState, selectedGroupId: group.id })
  }
  const handleChangeDestinationGroup = (e: OnChangeEventContext) => {
    const group = e.value as BoardGroupFragment
    setMoveGroupState({ ...moveGroupState, destinationGroupId: group.id })
  }
  const handleDestinationTypeChange = (value: string) => {
    setMoveGroupState({
      ...moveGroupState,
      destinationType: value
    })
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(moveGroupState)
  }

  const destGroups = groups.filter((g) => g.id !== moveGroupState.selectedGroupId)
  const selectedGroup = groups.find((g) => g.id === moveGroupState.selectedGroupId)
  const destinationGroup = groups.find((g) => g.id === moveGroupState.destinationGroupId)

  const renderer = ({ option }: any) =>
    `${option.title} ${
      option.termSequence !== null && option.termSequence !== 0 ? option.termSequence : ''
    }`

  return (
    <Modal allowMobilePage isOpen={isOpen} size={ModalSize.SM} onSubmit={handleSave}>
      <ModalHeader allowMobile onClose={onCancel} title={`Move ${termLabel}`} />
      <ModalBody allowMobile>
        <div css={[tertiaryBodyCss, upperFormCss]}>Select {termLabel} to Move</div>
        <Select
          id="group_to_move"
          css={dropdownCss}
          label={`${termLabel} to Move`}
          labelKey="title"
          mode={DropdownMode.Simple}
          onChange={handleChangeSelectedGroup}
          optionRenderer={renderer}
          options={groups}
          placeholder={`Select ${termLabel}`}
          srOnlyLabel
          value={moveGroupState.selectedGroupId}
          valueKey="id"
          valueRenderer={renderer}
        />
        <div css={lowerFormCss}>
          <RadioGroup
            ariaLabel={`Move ${termLabel.toLowerCase()} before or after`}
            onChange={handleDestinationTypeChange}
            options={radioOptions}
            selectedOption={moveGroupState.destinationType}
          />
          <Select
            id="destination_group"
            css={dropdownCss}
            label={`Destination ${termLabel}`}
            labelKey="title"
            mode={DropdownMode.Simple}
            onChange={handleChangeDestinationGroup}
            optionRenderer={renderer}
            options={destGroups}
            placeholder={`Select ${termLabel}`}
            srOnlyLabel
            value={moveGroupState.destinationGroupId}
            valueKey="id"
            valueRenderer={renderer}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <SecondaryButton ariaLabel={`Cancel moving ${termLabel.toLowerCase()}`} onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          ariaLabel={
            destinationGroup
              ? `Move ${termLabel} ${
                  selectedGroup?.title
                } ${moveGroupState.destinationType.toLowerCase()} ${destinationGroup.title}`
              : undefined
          }
          disabled={
            !moveGroupState.destinationGroupId ||
            moveGroupState.selectedGroupId === moveGroupState.destinationGroupId
          }
          type={ButtonType.Submit}
        >
          Move
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  )
}

export default MoveGroupModal
